// src/api/v3/admins-api-v3.js
import axios from 'axios'
import { API_BASE } from '../../configs/apiBase';

/** In-memory token; persisted in localStorage for reloads */
const TOKEN_KEY = 'bagspa_token'
let accessToken = null

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // JWT does not use cookies
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000
})

/** Helpers to manage the Authorization header */
const applyAuthHeader = (token) => {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`
  } else {
    delete api.defaults.headers.common.Authorization
  }
}

const setToken = (token) => {
  accessToken = token || null
  applyAuthHeader(accessToken)
  if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken)
  else localStorage.removeItem(TOKEN_KEY)
}

const getToken = () => accessToken || localStorage.getItem(TOKEN_KEY) || null

// Initialize from localStorage on module load
setToken(getToken())

/** Attach Authorization on every request (defensive) */
api.interceptors.request.use((config) => {
  const t = getToken()
  if (t) config.headers.Authorization = `Bearer ${t}`
  return config
})

/** 401 → try refresh (if the API supports it), then retry once */
let isRefreshing = false
let waiters = []

const notifyWaiters = (newToken) => {
  waiters.forEach((w) => w(newToken))
  waiters = []
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { config, response } = error
    if (!response) return Promise.reject(error) // network error

    if (response.status === 401 && !config.__isRetryRequest) {
      // queue retries while a refresh is in progress
      if (isRefreshing) {
        return new Promise((resolve) => {
          waiters.push((t) => {
            if (t) {
              config.__isRetryRequest = true
              config.headers.Authorization = `Bearer ${t}`
              resolve(api.request(config))
            } else {
              resolve(Promise.reject(error))
            }
          })
        })
      }

      // try refresh once
      try {
        isRefreshing = true
        const cur = getToken()
        if (!cur) throw new Error('no token to refresh')

        const r = await api.post('/api/v3/auth/refresh', null, {
          headers: { Authorization: `Bearer ${cur}` }
        })
        const newToken = r.data?.token
        setToken(newToken)
        notifyWaiters(newToken)

        config.__isRetryRequest = true
        config.headers.Authorization = `Bearer ${newToken}`
        return api.request(config)
      } catch (e) {
        setToken(null)
        notifyWaiters(null)
        return Promise.reject(e)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

/** Public API */
const AdminsApiV3 = {
  /** POST /auth/login -> { token, admin } */
  async login(email, password) {
    const res = await api.post('/api/v3/auth/login', { email, password })
    const { token, admin } = res.data || {}
    if (token) setToken(token)
    return { token, admin }
  },

  /** POST /auth/refresh -> { token }  (normally called by interceptor) */
  async refresh() {
    const cur = getToken()
    if (!cur) throw new Error('no token')
    const res = await api.post('/api/v3/auth/refresh', null, {
      headers: { Authorization: `Bearer ${cur}` }
    })
    const { token } = res.data || {}
    if (token) setToken(token)
    return token
  },

  /** GET /profile (JWT-protected) */
  async profile() {
    return api.get('/api/v3/profile')
  },

  /** PATCH /api/v3/password */
  async updatePassword({ current_password, new_password, password_confirmation }) {
    return api.patch('/api/v3/password', {
      current_password,
      new_password,
      password_confirmation
    });
  },

  async updatePassword({ current_password, new_password, password_confirmation }) {
    return api.patch('/api/v3/password', {
      current_password,
      new_password,
      password_confirmation
    })
  },
  
  async resetPassword() {
    // no body needed; server will use current_admin
    return api.post('/api/v3/password/reset')
  },

  /** Log out client-side (server doesn’t need to know for JWT) */
  async logout() {
    setToken(null)
  },

  /** Expose helpers for the provider */
  setToken,
  getToken,

  _api: api,
  API_BASE
}

export default AdminsApiV3
